// ArcGIS Server Admin API access with the signed-in administrator's portal token. SPEC 8.5 / 8.6.
// RESEARCH items: exact extension register/update endpoints, SOI typeName, restart behaviour.
import type { ArcGISIdentityManager } from "@esri/arcgis-rest-request";
import { request } from "@esri/arcgis-rest-request";

export interface FederatedServer {
  id: string;
  name: string;
  url: string;
  /** The admin endpoint this app can actually reach: the public URL through the Web Adaptor. */
  adminUrl: string;
  /**
   * What `portals/self/servers` reports. On a deployment behind a Web Adaptor this is the
   * server's own host and port (e.g. an internal `…:6443/arcgis`), routable from the server
   * machines but not from an administrator's browser. Kept because the SOI, which runs on
   * those machines, is configured with it.
   */
  internalAdminUrl: string;
  serverRole: string;
}
export interface ServiceSummary { folder: string; name: string; type: string; serviceKey: string; soiEnabled: boolean; soiPresent: boolean; }
/** What a service actually looks like after an administrative change (SPEC 8.5). */
export interface ServiceState {
  started: boolean;
  soiPresent: boolean;
  soiEnabled: boolean;
  configuredState: string;
}

export interface ExtensionInfo {
  registered: boolean;
  /**
   * Always null from the server: the extensions endpoint does not report a .soe's own version
   * (confirmed on 11.5). SPEC 8.11's version check must use the `status` table instead.
   */
  version: string | null;
  /** The .soe filename the server has registered, when it is ours. */
  soeFile?: string;
  /** The ArcGIS version the registered .soe targets, e.g. "11.5". */
  targetVersion?: string | null;
}

export const SOI_TYPE_NAME = "OrgFilterSOI"; // RESEARCH: confirm the typeName the SDK derives from the class name
export const SOI_DISPLAY_NAME = "GICHD Org Filter SOI";

export interface LayerInfo {
  id: number;
  name: string;
  type: string;
  fields: Array<{ name: string; type: string; alias?: string }>;
  relationships?: Array<{ id: number; relatedTableId: number; name?: string }>;
}

/**
 * Canonical form of a server URL for use in a service key (SPEC 2).
 *
 * The Manager and the SOI compute the service key independently and must produce byte-identical
 * strings, or the SOI looks up a policy row that was never written and denies everything, silently.
 * So the rules are fixed here and mirrored in the Java side: lower-case scheme and host, keep the
 * path's case, drop a default port, drop any trailing slash.
 */
export function normaliseServerUrl(url: string): string {
  const trimmed = (url ?? "").trim().replace(/\/+$/, "");
  try {
    const u = new URL(trimmed);
    const port = (u.protocol === "https:" && u.port === "443") || (u.protocol === "http:" && u.port === "80") ? "" : u.port;
    const path = u.pathname.replace(/\/+$/, "");
    return `${u.protocol.toLowerCase()}//${u.hostname.toLowerCase()}${port ? `:${port}` : ""}${path}`;
  } catch {
    return trimmed; // not a URL we can parse; the caller's string is all we have
  }
}

/**
 * SPEC 2, as amended: `<publicServerUrl>|<folder>/<name>.<type>`, e.g.
 * `https://gis.example.org/server|EORE/Reports.MapServer`.
 *
 * The public URL rather than the admin URL, because behind a Web Adaptor the admin URL that Portal
 * reports is an internal host the administrator never sees, and this key is shown to them in the
 * policies, audit and status tables.
 */
export function serviceKey(serverUrl: string, folder: string, name: string, type: string): string {
  const path = folder ? `${folder}/${name}.${type}` : `${name}.${type}`;
  return `${normaliseServerUrl(serverUrl)}|${path}`;
}

export function splitServiceKey(key: string): { serverUrl: string; folder: string; name: string; type: string } {
  const [serverUrl, path = ""] = key.split("|");
  const slash = path.lastIndexOf("/");
  const folder = slash >= 0 ? path.slice(0, slash) : "";
  const leaf = slash >= 0 ? path.slice(slash + 1) : path;
  const dot = leaf.lastIndexOf(".");
  return { serverUrl, folder, name: leaf.slice(0, dot), type: leaf.slice(dot + 1) };
}

/** Just the host part of a URL, for display. */
function hostOf(url: string): string {
  try { return new URL(url).hostname; } catch { return ""; }
}

export function servicePath(svc: { folder: string; name: string; type: string }): string {
  return svc.folder ? `${svc.folder}/${svc.name}.${svc.type}` : `${svc.name}.${svc.type}`;
}

export class AdminApi {
  constructor(private readonly session: ArcGISIdentityManager, private readonly portalUrl: string) {}

  private get(url: string, params: Record<string, unknown> = {}): Promise<any> {
    return request(url, { authentication: this.session, httpMethod: "GET", params: { f: "json", ...params } });
  }

  private post(url: string, params: Record<string, unknown> = {}): Promise<any> {
    return request(url, { authentication: this.session, httpMethod: "POST", params: { f: "json", ...params } });
  }

  /**
   * Admin endpoints sit outside `/rest/`, and arcgis-rest-js works out which federated server a
   * URL belongs to by looking for `/rest/services` in it. It therefore cannot federate a token for
   * an admin URL on its own, and every admin call comes back 499 "Token not found". Resolving the
   * token against the server's REST root and attaching it explicitly is what makes these work.
   */
  private async serverToken(server: FederatedServer): Promise<string> {
    return this.session.getToken(`${server.url}/rest/services`);
  }

  private async adminGet(server: FederatedServer, path: string, params: Record<string, unknown> = {}): Promise<any> {
    const token = await this.serverToken(server);
    return request(`${server.adminUrl}${path}`, { httpMethod: "GET", params: { f: "json", token, ...params } });
  }

  private async adminPost(server: FederatedServer, path: string, params: Record<string, unknown> = {}): Promise<any> {
    const token = await this.serverToken(server);
    return request(`${server.adminUrl}${path}`, { httpMethod: "POST", params: { f: "json", token, ...params } });
  }

  /** portals/self/servers */
  async federatedServers(): Promise<FederatedServer[]> {
    const res = await this.get(`${this.portalUrl.replace(/\/+$/, "")}/sharing/rest/portals/self/servers`);
    return (res.servers ?? []).map((s: any) => {
      const url = (s.url ?? "").replace(/\/+$/, "");
      return {
        id: s.id,
        // NOT s.name: Portal reports the server's own internal host and port there
        // (e.g. …ax.internal.cloudapp.net:6443), which no administrator recognises. The public
        // host is what they see in every URL and what the service key is built from.
        name: hostOf(url) || s.name || url,
        url,
        // Deliberately NOT s.adminUrl: see FederatedServer.internalAdminUrl. The Web Adaptor
        // must have administrative access enabled for this to answer.
        adminUrl: `${url}/admin`,
        internalAdminUrl: (s.adminUrl ?? "").replace(/\/+$/, ""),
        serverRole: s.serverRole ?? ""
      };
    });
  }

  /**
   * <adminUrl>/services/types/extensions -> is our SOI registered.
   *
   * Confirmed against ArcGIS Enterprise 11.5: the response is NOT `{ extensions: [...] }`. It is an
   * object keyed by the .soe filename, each value an array of the extension types that file
   * contains, in the shape the SDK writes into Config.xml:
   *
   *   { "GICHD_OrgFilter.soe": [ { Name, DisplayName, CLSID, ParentServiceType,
   *                                Info: { SupportsInterceptor, SupportsSharedInstances, ... },
   *                                provider, targetVersion } ] }
   *
   * `Name` is the typeName (the simple class name) and `targetVersion` is the ArcGIS version the
   * .soe targets — 11.5 — NOT the SOI's own build version, which this endpoint does not report at
   * all. The deployed version therefore has to come from the `status` table the SOI writes
   * (SPEC 4.4), which is the only place it exists on the server side.
   */
  async extensionInfo(server: FederatedServer): Promise<ExtensionInfo> {
    const res = await this.adminGet(server, "/services/types/extensions");
    for (const [soeFile, types] of Object.entries(res ?? {})) {
      if (!Array.isArray(types)) continue;
      for (const t of types as any[]) {
        if (t?.Name === SOI_TYPE_NAME || t?.DisplayName === SOI_DISPLAY_NAME) {
          return { registered: true, version: null, soeFile, targetVersion: t?.targetVersion ?? null };
        }
      }
    }
    return { registered: false, version: null };
  }

  /** uploads/upload + services/types/extensions/register|update. */
  async registerOrUpdateSoi(server: FederatedServer, soe: Blob, update: boolean): Promise<void> {
    const upload = await request(`${server.adminUrl}/uploads/upload`, {
      httpMethod: "POST",
      params: {
        f: "json",
        token: await this.serverToken(server),
        itemFile: new File([soe], "GICHD_OrgFilter.soe", { type: "application/octet-stream" })
      }
    });
    const itemId = upload?.item?.itemID ?? upload?.itemID;
    if (!itemId) throw new Error("The server accepted the upload but returned no item id");
    // update restarts every service that has the SOI enabled; the caller warns first.
    await this.adminPost(server, `/services/types/extensions/${update ? "update" : "register"}`, { id: itemId });
  }

  /**
   * Services that use the SOI but are not currently in a state to filter: stopped, or no longer
   * carrying the extension. Run after a register or update, which have both been observed leaving
   * services in exactly those states without saying so (SPEC 8.5).
   */
  async servicesNeedingAttention(server: FederatedServer): Promise<Array<{ svc: ServiceSummary; state: ServiceState }>> {
    const services = await this.listServices(server);
    const out: Array<{ svc: ServiceSummary; state: ServiceState }> = [];
    for (const svc of services) {
      if (!svc.soiPresent) continue;
      const state = await this.serviceState(server, svc);
      if (!state.started || !state.soiEnabled) out.push({ svc, state });
    }
    return out;
  }

  async unregisterSoi(server: FederatedServer): Promise<void> {
    await this.adminPost(server, "/services/types/extensions/unregister", { extensionFilename: "GICHD_OrgFilter.soe" });
  }

  /**
   * Recursive listing of all services and whether the SOI extension is enabled on each.
   *
   * The listing itself carries only `folderName`, `serviceName`, `type` and `description` — it has
   * no `extensions` array (confirmed on 11.5), so reading the SOI state from it silently reported
   * every service as "not added". It has to come from each service's own definition, which is one
   * request per map service; they are issued in small batches so a site with many services does
   * not open a hundred connections at once.
   */
  async listServices(server: FederatedServer): Promise<ServiceSummary[]> {
    const root = await this.adminGet(server, "/services");
    const folders: string[] = ["", ...((root.folders ?? []) as string[]).filter((f) => f !== "/" && f !== "System" && f !== "Utilities")];
    const found: ServiceSummary[] = [];
    for (const folder of folders) {
      const listing = folder === "" ? root : await this.adminGet(server, `/services/${folder}`);
      for (const s of (listing.services ?? []) as any[]) {
        // Only map services can carry an interceptor (SPEC 6.1).
        if (s.type !== "MapServer") continue;
        found.push({
          folder,
          name: s.serviceName,
          type: s.type,
          serviceKey: serviceKey(server.url, folder, s.serviceName, s.type),
          soiPresent: false,
          soiEnabled: false
        });
      }
    }

    const BATCH = 6;
    for (let i = 0; i < found.length; i += BATCH) {
      await Promise.all(found.slice(i, i + BATCH).map(async (svc) => {
        try {
          const state = await this.serviceState(server, svc);
          svc.soiPresent = state.soiPresent;
          svc.soiEnabled = state.soiEnabled;
        } catch {
          // A service that will not describe itself is left as "not added" rather than failing
          // the whole listing.
        }
      }));
    }
    return found;
  }

  async serviceJson(server: FederatedServer, svc: ServiceSummary): Promise<any> {
    return this.adminGet(server, `/services/${servicePath(svc)}`);
  }

  /**
   * What the service looks like now: started, and is our extension on it?
   *
   * Administrative operations do not reliably leave a service as they found it — registering an
   * updated .soe has been observed to leave the service STOPPED, and changing instance pooling
   * resets the extension's enabled flag — and neither is reported by the operation that caused it
   * (SPEC 8.5). A stopped service, or one that has quietly lost its extension, is not a filtered
   * service, so the Manager reads the state back rather than trusting the call it just made.
   */
  async serviceState(server: FederatedServer, svc: ServiceSummary): Promise<ServiceState> {
    const json = await this.serviceJson(server, svc);
    const ext = (json.extensions ?? []).find((e: any) => e.typeName === SOI_TYPE_NAME);
    return {
      started: json.configuredState === "STARTED",
      soiPresent: !!ext,
      soiEnabled: ext ? ext.enabled === true || ext.enabled === "true" : false,
      configuredState: json.configuredState ?? "unknown"
    };
  }

  /**
   * GET the service JSON, add or flip our extension, POST the COMPLETE JSON back.
   * The Admin API replaces the whole definition, so anything dropped here is lost;
   * that is why the untouched document is sent back rather than a patch.
   * The service restarts as a result (SPEC 8.6).
   *
   * @returns the state read back afterwards, so the caller can report a change that did not take.
   */
  async setSoiEnabled(server: FederatedServer, svc: ServiceSummary, enabled: boolean): Promise<ServiceState> {
    const json = await this.serviceJson(server, svc);
    const extensions: any[] = json.extensions ?? (json.extensions = []);
    const existing = extensions.find((e) => e.typeName === SOI_TYPE_NAME);
    if (existing) {
      existing.enabled = enabled;
    } else {
      // Nothing to disable, but still report what is actually there.
      if (!enabled) return this.serviceState(server, svc);
      // Appended last so it intercepts after any other registered interceptor.
      extensions.push({ typeName: SOI_TYPE_NAME, enabled: true, capabilities: "", properties: {} });
    }
    await this.adminPost(server, `/services/${servicePath(svc)}/edit`, { service: JSON.stringify(json) });
    return this.serviceState(server, svc);
  }

  /** <adminUrl>/machines */
  async machines(server: FederatedServer): Promise<string[]> {
    const res = await this.adminGet(server, "/machines");
    return ((res.machines ?? []) as any[]).map((m) => m.machineName ?? m.name).filter(Boolean);
  }

  /** REST (not admin) layers?f=json for the policy editor's layer table and test-as-user. */
  async layers(server: FederatedServer, svc: ServiceSummary): Promise<LayerInfo[]> {
    const res = await this.get(`${this.restUrl(server, svc)}/layers`);
    const all = [...((res.layers ?? []) as any[]), ...((res.tables ?? []) as any[])];
    return all.map((l) => ({
      id: l.id,
      name: l.name,
      type: l.type ?? "Feature Layer",
      fields: ((l.fields ?? []) as any[]).map((f) => ({ name: f.name, type: f.type, alias: f.alias })),
      relationships: ((l.relationships ?? []) as any[]).map((r) => ({ id: r.id, relatedTableId: r.relatedTableId, name: r.name }))
    }));
  }

  restUrl(server: FederatedServer, svc: { folder: string; name: string; type: string }): string {
    const folder = svc.folder ? `${svc.folder}/` : "";
    return `${server.url}/rest/services/${folder}${svc.name}/${svc.type}`;
  }

  /** SPEC 8.8 step 4: how many rows the user would actually see. */
  async count(layerUrl: string, where: string): Promise<number> {
    const res = await this.get(`${layerUrl}/query`, { where, returnCountOnly: true });
    return res.count ?? 0;
  }
}
